import { Injectable } from '@angular/core';
import { filter, Observable, switchMap } from 'rxjs';
import { CollectionAttributes, AdvisorResponse } from '../models/advisor.interface';
import { S3Service } from './s3.service';
import { BedrockService } from './bedrock.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ConfigurationService } from './configuration.service';
import { DriftConfiguration } from '../models/configuration.interface';
import { ConversationHistory, SingleSessionResponse } from '../models/conversations.interface';

@Injectable({
  providedIn: 'root'
})
export class ConversationsService {
  currentPrompt = '';
  config: DriftConfiguration;
  constructor(
    private http: HttpClient,
    private configurationService: ConfigurationService,
    private s3Service: S3Service,
    private bedrockService: BedrockService
  ) {
    this.config = {};

    this.configurationService
      .configurationLoaded()
      .pipe(filter(loaded => loaded === true))
      .subscribe(() => {
        this.config = this.configurationService.getConfiguration();
      });
  }

  public loadSession(session_id: string): Observable<SingleSessionResponse> {
    const params = new HttpParams().set('session_id', session_id);
    this.config = this.configurationService.getConfiguration();
    return this.http.get<SingleSessionResponse>(`${this.config.driftApi}/conversation`, { params });
  }

  public loadConversations(collection_id: string): Observable<ConversationHistory[]> {
    const params = new HttpParams().set('collection_id', collection_id);
    this.config = this.configurationService.getConfiguration();
    return this.http.get<ConversationHistory[]>(`${this.config.driftApi}/conversations`, { params });
  }

  public sendMessage(
    model_id: string,
    collection_id: string,
    sessionId: string | null = null,
    message: string,
    activeFilters: CollectionAttributes
  ): Observable<AdvisorResponse> {
    return this.bedrockService
      .invokeBedrockModelWithContext(sessionId, message, model_id, collection_id, activeFilters)
      .pipe(
        switchMap(response => {
          const filename = response.filename;
          this.currentPrompt = message;
          return this.pollForResponse(filename);
        })
      );
  }

  private pollForResponse(filename: string): Observable<AdvisorResponse> {
    const maxAttempts = 30;
    const pollInterval = 2000; // 2 seconds

    let attempts = 0;

    // Create an observable that polls until we get a complete response or exceed maxAttempts
    return new Observable<AdvisorResponse>(observer => {
      // A single poll attempt
      const poll = () => {
        if (attempts >= maxAttempts) {
          observer.error(new Error('Response timed out after maximum polling attempts'));
          return;
        }

        attempts++;

        this.s3Service.pollResult(filename).subscribe({
          next: response => {
            if (response && response.body?.complete === 'true') {
              const result: AdvisorResponse = {
                session_id: response.body.session_id,
                collection_id: response.body.collection_id,
                complete: response.body.complete,
                filename: filename,
                metrics: response.body.metrics || {},
                prompt: this.currentPrompt,
                response: response.body.response,
                source_documents: this.processSourceDocuments(response.body.source_documents),
                user: response.body.user
              };

              observer.next(result);
              observer.complete();
            } else {
              // Not complete yet, schedule another poll
              setTimeout(poll, pollInterval);
            }
          },
          error: error => {
            // If this is a fatal error or we've reached max attempts, propagate it
            if (attempts >= maxAttempts) {
              observer.error(error);
            } else {
              // Otherwise, continue polling
              setTimeout(poll, pollInterval);
            }
          }
        });
      };

      poll();

      // Cancel polling if the subscription is unsubscribed
      return () => {
        attempts = maxAttempts; // This will prevent further polling
      };
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private processSourceDocuments(documents: any[]): any[] {
    if (!documents || !Array.isArray(documents)) {
      return [];
    }

    return documents
      .map(doc => {
        if (doc.metadata && doc.metadata.source) {
          doc.metadata.source = doc.metadata.source.split('\\').pop().split('/').pop();
        }
        return doc;
      })
      .sort((a, b) => b.score - a.score);
  }
}
